<script setup lang="ts">
import { defineAsyncComponent, hydrateOnVisible } from 'vue'

import PrintFooter from './content-footer/PrintFooter.vue'
import SocialShare from './content-footer/SocialShare.vue'
import Advertorial from './content-header/Advertorial.vue'
import AffiliateDisclaimer from './content-header/AffiliateDisclaimer.vue'
import Author from './content-header/Author.vue'
import VodEpgBadges from './shared/vod-epg/Badges.vue'
import MetadataHeader from './content-header/MetadataHeader.vue'
import Breadcrumb from './content-header/Breadcrumb.vue'
import Headline from './content-header/Headline.vue'
import IntroductionText from './content-header/IntroductionText.vue'
import Message from './content-header/Message.vue'
import PrintHeader from './content-header/PrintHeader.vue'
import PrintUsage from './content-header/PrintUsage.vue'
import Roofline from './content-header/Roofline.vue'
import HotGossip from './menu/HotGossip.vue'
import PageAside from './PageAside.vue'
import PagePagination from './PagePagination.vue'
import { ParagraphTypeHtml } from './paragraph-types'
import Paragraphs from './Paragraphs.vue'
import SeriesName from './series/SeriesName.vue'
import SeriesNavigationBottom from './series/SeriesNavigationBottom.vue'
import SeriesNavigationTop from './series/SeriesNavigationTop.vue'
import Tags from './shared/Tags.vue'
import TrackingPixel from './tracking/TrackingPixel.vue'
import Presenter from './shared/Presenter.vue'
import AdaNativeHeader from './ad-slots/AdaNativeHeader.vue'
import AdaPagestripMain from './ad-slots/AdaPagestripMain.vue'
import AdaNativeCrossbar from './ad-slots/AdaNativeCrossbar.vue'
import { usePageContentSharedSetup } from '../composables/use-page-content-shared-setup'
import { usePageContentShared } from '../composables/use-page-content-shared'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedBody, ComposedMetaData, ComposedPage, ComposedPageMeta } from '@xcel-next/composer-schema'

// `metaData.seeding` is injected by the rendering layer, not part of composer-schema's ComposedMetaData
type PageContentMetaData = ComposedMetaData & {
  seeding?: { enabled?: boolean, headlineId?: string, articleId?: string }
}

const EpgDetailHeader = defineAsyncComponent({
  loader: () => import('./paragraph-types/ParagraphTypeEpgDetailHeader.vue'),
  hydrate: hydrateOnVisible()
})

const props = withDefaults(defineProps<{
  // The asset object holding all page data from the CAPI/Composer
  asset?: ComposedBody,
  brand?: Brand,
  pageMeta?: ComposedPageMeta,
  headerData?: NonNullable<ComposedPage['header']>,
  metaData?: PageContentMetaData,
  // eslint-disable-next-line vue/no-unused-properties
  taboolaEnabled?: boolean
}>(), {
  asset: () => ({}),
  pageMeta: () => ({}),
  headerData: () => ({}),
  metaData: () => ({}),
  taboolaEnabled: false
})

const setupDeps = usePageContentSharedSetup(props)
const { isClient, enableNcbInDynamicMode, showNcbTopBelowArticle } = setupDeps
const {
  isRootUrl,
  showHeadline,
  showMessage,
  showAuthors,
  combineAuthors,
  assetTags,
  isPrintPreview,
  isArticleOrArticleVersion,
  rsConfig,
  asideEnabled
} = usePageContentShared(props, setupDeps)
</script>

<template>
  <main id="main-content"
        class="asset-container bx-grid--container">
    <PrintHeader v-if="isPrintPreview"
                 :brand="brand" />
    <Message v-if="showMessage" />
    <slot name="content-top" />
    <Presenter v-if="metaData?.presenter?.imageId"
               :presenter="metaData.presenter"
               :brand="brand" />
    <AdaNativeHeader v-if="metaData?.nativeHeader"
                     :native-header="metaData.nativeHeader"
                     :brand="brand" />
    <HotGossip v-if="headerData.additionalMenu && !metaData.seeding"
               :additional-menu="headerData.additionalMenu"
               :brand="brand" />
    <Breadcrumb v-if="asset.mainStructurePath && !isRootUrl"
                :breadcrumb="asset.mainStructurePath"
                :page-type="pageMeta.pageType ?? ''" />
    <Advertorial v-if="metaData && metaData.advertorial && !metaData.hideAdvertorial" />
    <TrackingPixel v-if="metaData && metaData.trackingPixelContent"
                   :tracking-pixel="metaData.trackingPixelContent" />
    <TrackingPixel v-if="metaData && metaData.respondiTrackingPixelContent"
                   :tracking-pixel="metaData.respondiTrackingPixelContent" />

    <EpgDetailHeader v-if="asset.additionalHeaders?.metadata?.top?.channel"
                     :additional-headers="asset.additionalHeaders"
                     :roofline="asset.roofline"
                     :headline="asset.headline"
                     :seeding="metaData.seeding" />
    <template v-else>
      <MetadataHeader v-if="asset.additionalHeaders?.metadata?.top"
                      :meta-infos="asset.additionalHeaders.metadata.top" />
      <VodEpgBadges v-if="asset.additionalHeaders?.badges"
                    :badges="asset.additionalHeaders.badges" />
      <Roofline v-if="asset.roofline && asset.roofline.length > 0"
                :text="asset.roofline" />
      <Headline v-if="showHeadline"
                :seeding="metaData.seeding"
                :text="asset.headline ?? ''" />
      <MetadataHeader v-if="asset.additionalHeaders?.metadata?.bottom"
                      :meta-infos="asset.additionalHeaders.metadata.bottom" />
    </template>

    <Author v-if="showAuthors"
            :composed-authors="combineAuthors"
            :settings="{
              brand,
              displayDate: metaData.hideDisplayDate ? false : asset.displayDate
            }" />

    <AdaPagestripMain v-if="metaData?.pagestripMainId"
                      :pagestrip-id="metaData.pagestripMainId" />

    <SeriesName v-if="asset.series && pageMeta.isSeriesMember"
                :series-master="asset.series[0]"
                :asset-id="pageMeta.id" />
    <IntroductionText v-if="asset.introductionText && asset.introductionText.length > 0"
                      :html="asset.introductionText" />
    <SocialShare v-if="isArticleOrArticleVersion && (!metaData.advertorial || (metaData.advertorial && metaData.hideAdvertorial)) && rsConfig?.socialSettings?.socialShare?.enableTopPosition"
                 :page-meta="pageMeta"
                 :headline="asset.headline" />
    <SeriesNavigationTop v-if="asset.series"
                         :series="asset.series"
                         :page-meta="pageMeta" />
    <section :class="{ 'bx-aside-wrapper': asideEnabled }">
      <div>
        <Paragraphs :paragraphs="asset.paragraphs"
                    :meta-data="metaData"
                    :page-meta="pageMeta"
                    :brand="brand"
                    :page-type="pageMeta.pageType"
                    :include-depth="0">
          <!-- eslint-disable vue/valid-v-slot -->
          <template v-for="(_, slot) of $slots"
                    #[slot]="scope">
            <slot :name="slot"
                  v-bind="scope" />
          </template>
          <!-- eslint-enable vue/valid-v-slot -->
        </Paragraphs>
        <PrintUsage v-if="metaData.printUsage"
                    :print-usage="metaData.printUsage" />
        <SeriesNavigationBottom v-if="asset.series && pageMeta"
                                :series="asset.series"
                                :page-meta="pageMeta" />

        <AffiliateDisclaimer v-if="metaData.showAffiliateDisclaimer || metaData.showVodStreamingDisclaimer" />

        <Tags v-if="isArticleOrArticleVersion && assetTags.length > 0"
              :tags="assetTags" />

        <PagePagination v-if="(pageMeta.numPages ?? 0) > 1"
                        :page-meta="pageMeta" />
        <slot name="before-seo-text" />
        <ParagraphTypeHtml v-if="asset.seoText"
                           :meta-data="metaData"
                           :html="asset.seoText" />
      </div>
      <PageAside v-if="asideEnabled"
                 :brand="brand" />
    </section>
    <AdaNativeCrossbar v-if="isClient && enableNcbInDynamicMode && showNcbTopBelowArticle"
                       position="top" />
    <PrintFooter v-if="isPrintPreview" />
  </main>
</template>
